// ✅ src/pages/VehicleV2/BatteryCardReport_AllPrint.jsx
// Battery Card Maintenance Report — All Print
// Logo: src/assets/infraLogo.jpg
//
// API row keys:
// VehicleType, currentdate, fld_BranchName,
// fld_Fitteddate, fld_Vehiclename, fld_vehicle_regno,
// fld_batterycode, fld_batterytype, fld_battery_serialno,
// fld_actuallife, fld_currentlife, fld_perdaycost, alert
//
// Endpoint:
//   GET /api/batteryCardMaintenanceReport/list

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import companyLogo from "../../assets/infraLogo.jpg";

const API_BASE = "https://nissi-india.com/api/batteryCardMaintenanceReport";

const toStr = (v) => String(v ?? "").trim();
const isSelectLike = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select" || s === "all";
};
const pretty = (v) => (isSelectLike(v) ? "ALL" : toStr(v));
const display = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "—" : v;

function mapFiltersToParams(filters) {
  return {
    branchType: toStr(filters.branchType || filters.branchtype || "HO"),
    selectedBranchId: toStr(
      filters.selectedBranchId || filters.branch || "Select"
    ),
    loginUserId: toStr(filters.loginUserId || filters.loginuserid || ""),
    fromDate: toStr(filters.fromDate || ""),
    toDate: toStr(filters.toDate || ""),
    vehicleCode: toStr(filters.vehicleCode || ""),
    vehicleName: toStr(filters.vehicleName || ""),
    vehicleRegNo: toStr(filters.vehicleRegNo || filters.vehicleRegno || ""),
    batteryCode: toStr(filters.batteryCode || "Select"),
    batteryType: toStr(filters.batteryType || "Select"),
    batterySerialNo: toStr(
      filters.batterySerialNo || filters.serialNo || "Select"
    ),
    batteryStatus: toStr(filters.batteryStatus || "Select"),
    vehicleMainTypeIndex: toStr(filters.vehicleMainTypeIndex || "Select"),
    vehicleStatus: toStr(filters.vehicleStatus || "Select"),
    store: toStr(filters.store || "Select"),
  };
}

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtDateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    const date = d.toLocaleDateString("en-US");
    const time = d.toLocaleTimeString("en-US");
    return `${date} ${time}`;
  }
  return String(v);
};

const fmtDateOnly = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-US");
  return String(v);
};

const BatteryCardMaintenance_AllPrint = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const filters = useMemo(() => location?.state?.filters || {}, [location]);
  const params = useMemo(() => mapFiltersToParams(filters), [filters]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  const finYear =
    typeof window !== "undefined" ? localStorage.getItem("finYear") || "" : "";

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/list`, { params });
        const apiRows = res?.data?.rows || [];

        const normalized = apiRows.map((r, i) => ({
          sno: i + 1,
          vehicleMainType: toStr(r.VehicleType),
          vehicleName: toStr(r.fld_Vehiclename),
          vehicleRegNo: toStr(r.fld_vehicle_regno),
          batteryCode: toStr(r.fld_batterycode),
          batteryType: toStr(r.fld_batterytype),
          batterySerialNo: toStr(r.fld_battery_serialno),
          fittedDate: fmtDateOnly(r.fld_Fitteddate),
          currentRemoveDate: fmtDateTime(r.currentdate),
          actualLife: toStr(r.fld_actuallife),
          currentLife: toStr(r.fld_currentlife),
          perDayCost: fmtMoney(r.fld_perdaycost),
          branchName: toStr(r.fld_BranchName),
          alert: toStr(r.alert),
        }));

        if (alive) setRows(normalized);
      } catch (e) {
        if (alive)
          setErr(
            e?.response?.data?.message || e.message || "Failed to load print data"
          );
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  const currentDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const currentDateTime = useMemo(
    () =>
      new Date().toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  const dateRange =
    params.fromDate || params.toDate
      ? `${params.fromDate || "—"} to ${params.toDate || "—"}`
      : "ALL";

  return (
    <div className="report-page w-full bg-white">
      <style>{`
        :root {
          --navy: #163a5f;
          --navy-deep: #0f2a45;
          --gold: #e8a317;
          --slate: #5b6b82;
          --line: #c5ced8;
          --ink: #1c2430;
        }

        .report-page {
          color: var(--ink);
          font-family: "Segoe UI", Calibri, Arial, sans-serif;
        }

        .letterhead {
          border: 1.5px solid var(--navy);
          overflow: hidden;
          background: #fff;
        }

        .letterhead-topbar {
          height: 6px;
          background: linear-gradient(90deg, #0f2a45 0%, #163a5f 55%, #e8a317 100%);
        }

        .letterhead-body {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 10px 14px 8px;
        }

        .logo-wrap {
          flex: 0 0 auto;
          width: 82px;
          height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-wrap img {
          max-width: 82px;
          max-height: 70px;
          object-fit: contain;
          display: block;
        }

        .company-block {
          flex: 1 1 auto;
          text-align: center;
          min-width: 0;
        }

        .brand-name {
          font-size: 20px;
          letter-spacing: 1.4px;
          font-weight: 800;
          color: var(--navy);
          line-height: 1.1;
          margin: 0;
        }

        .legal-name {
          margin: 2px 0 0;
          font-size: 12px;
          font-weight: 600;
          color: #2c3e50;
        }

        .addr-line {
          margin: 3px 0 0;
          font-size: 10.5px;
          color: #4b5563;
          line-height: 1.4;
        }

        .meta-block {
          flex: 0 0 150px;
          text-align: right;
          font-size: 10.5px;
          color: #334155;
          line-height: 1.35;
        }

        .meta-label {
          display: block;
          font-size: 8.5px;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: var(--slate);
          font-weight: 700;
        }

        .meta-value {
          display: block;
          font-weight: 700;
          color: var(--navy);
        }

        .letterhead-bottombar {
          height: 3px;
          background: var(--gold);
        }

        .doc-title-wrap {
          margin: 12px 0 8px;
          text-align: center;
        }

        .doc-title {
          margin: 0;
          font-size: 16px;
          font-weight: 800;
          letter-spacing: 1.2px;
          color: var(--navy);
        }

        .doc-sub {
          margin: 2px 0 0;
          font-size: 10.5px;
          color: var(--slate);
        }

        .title-rule {
          width: 80px;
          height: 3px;
          background: var(--gold);
          margin: 6px auto 0;
        }

        .filter-strip {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          border: 1px solid var(--line);
          background: #f6f8fb;
          margin-bottom: 10px;
        }

        .filter-cell {
          padding: 6px 8px;
          border-right: 1px solid var(--line);
        }

        .filter-cell:last-child {
          border-right: none;
        }

        .filter-k {
          display: block;
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: var(--slate);
        }

        .filter-v {
          display: block;
          margin-top: 1px;
          font-size: 11px;
          font-weight: 700;
          color: var(--navy);
          word-break: break-word;
        }

        .rpt-table {
          width: 100%;
          border-collapse: collapse;
        }

        .rpt-table th {
          background: var(--navy);
          color: #fff;
          font-size: 8px;
          letter-spacing: 0.15px;
          font-weight: 700;
          padding: 5px 3px;
          border: 1px solid var(--navy-deep);
          text-align: center;
          line-height: 1.2;
        }

        .rpt-table td {
          border: 1px solid #d0d7e0;
          padding: 4px 3px;
          font-size: 8px;
          color: #1f2937;
          text-align: center;
          vertical-align: middle;
        }

        .rpt-table td.left {
          text-align: left;
        }

        .rpt-table td.right {
          text-align: right;
        }

        .rpt-table tbody tr:nth-child(even) {
          background: #f7f9fc;
        }

        .sign-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-top: 16px;
          gap: 24px;
        }

        .gen-by {
          font-size: 10.5px;
          color: #475569;
          line-height: 1.45;
        }

        .sign-box {
          width: 190px;
          text-align: center;
        }

        .sign-line {
          border-top: 1px solid #64748b;
          padding-top: 5px;
          font-size: 11px;
          font-weight: 700;
          color: var(--navy);
        }

        .sign-hint {
          font-size: 9.5px;
          color: #64748b;
          margin-top: 2px;
        }

        .rpt-footer {
          margin-top: 12px;
          border-top: 1px solid var(--line);
          padding-top: 7px;
          display: flex;
          justify-content: space-between;
          font-size: 9.5px;
          color: #64748b;
        }

        .disclaimer {
          text-align: center;
          font-size: 9.5px;
          color: #64748b;
          margin-top: 6px;
        }

        @media print {
          @page {
            size: A4 landscape;
            margin: 7mm 6mm 8mm 6mm;
          }

          html,
          body {
            background: #fff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .no-print {
            display: none !important;
          }

          .report-sheet {
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .letterhead,
          .filter-strip,
          .sign-row {
            break-inside: avoid;
          }

          .rpt-table thead {
            display: table-header-group;
          }

          .rpt-table tbody tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .rpt-table th {
            background: #163a5f !important;
            color: #fff !important;
          }

          .rpt-table tbody tr:nth-child(even) {
            background: #f7f9fc !important;
          }
        }
      `}</style>

      <div className="report-sheet p-4 print:p-0">
        <div className="no-print flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
            type="button"
          >
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-[#163a5f] hover:bg-[#0f2a45] text-white font-semibold rounded shadow"
            type="button"
          >
            Print Report
          </button>
        </div>

        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm mb-2">
            Loading report...
          </div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700 mb-2">
            {err}
          </div>
        ) : null}

        <header className="letterhead">
          <div className="letterhead-topbar" />
          <div className="letterhead-body">
            <div className="logo-wrap">
              <img
                src={companyLogo}
                alt="INFRACONSOFT"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <div className="company-block">
              <h1 className="brand-name">INFRACONSOFT</h1>
              <p className="legal-name">
                Nissi Infotech &nbsp;|&nbsp; Nissi Civil Construction
              </p>
              <p className="addr-line">
                3/42-12, Nagappa Colony, Kavundampalayam — 641030, Tamil Nadu, India
                <br />
                Phone: +91 90037 44644 &nbsp;·&nbsp;
                infraconsoft-implementation@nissiinfotech.in
              </p>
            </div>

            <div className="meta-block">
              <span className="meta-label">Report Date</span>
              <span className="meta-value">{currentDate}</span>
              <span className="meta-label" style={{ marginTop: 7 }}>
                Financial Year
              </span>
              <span className="meta-value">{finYear || "—"}</span>
              <span className="meta-label" style={{ marginTop: 7 }}>
                Document
              </span>
              <span className="meta-value">BCM / Maint. Register</span>
            </div>
          </div>
          <div className="letterhead-bottombar" />
        </header>

        <div className="doc-title-wrap">
          <h2 className="doc-title">BATTERY CARD MAINTENANCE REPORT</h2>
          <p className="doc-sub">Battery maintenance register — filtered listing</p>
          <div className="title-rule" />
        </div>

        <section className="filter-strip">
          <div className="filter-cell">
            <span className="filter-k">Branch</span>
            <span className="filter-v">{pretty(params.selectedBranchId)}</span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Period</span>
            <span className="filter-v">{dateRange}</span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Battery Type</span>
            <span className="filter-v">{pretty(params.batteryType)}</span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Battery Status</span>
            <span className="filter-v">{pretty(params.batteryStatus)}</span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Vehicle Status</span>
            <span className="filter-v">{pretty(params.vehicleStatus)}</span>
          </div>
          <div className="filter-cell">
            <span className="filter-k">Records</span>
            <span className="filter-v">{loading ? "…" : rows.length}</span>
          </div>
        </section>

        <table className="rpt-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Vehicle Main Type</th>
              <th>Vehicle Name</th>
              <th>Vehicle Reg No</th>
              <th>Battery Code</th>
              <th>Battery Type</th>
              <th>Battery Serial No</th>
              <th>Fitted Date</th>
              <th>Current/Remove Date</th>
              <th>Actual life</th>
              <th>Current life</th>
              <th>Per Day Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={12}>No records found for selected filters.</td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{r.sno}</td>
                  <td className="left">{display(r.vehicleMainType)}</td>
                  <td className="left">{display(r.vehicleName)}</td>
                  <td>{display(r.vehicleRegNo)}</td>
                  <td className="left">{display(r.batteryCode)}</td>
                  <td>{display(r.batteryType)}</td>
                  <td>{display(r.batterySerialNo)}</td>
                  <td>{display(r.fittedDate)}</td>
                  <td>{display(r.currentRemoveDate)}</td>
                  <td>{display(r.actualLife)}</td>
                  <td>{display(r.currentLife)}</td>
                  <td className="right">{display(r.perDayCost)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="sign-row">
          <div className="gen-by">
            <strong style={{ color: "#163a5f" }}>Prepared by</strong>
            <br />
            Infraconsoft ERP
            <br />
            Automated battery maintenance register
            <br />
            Generated: {currentDateTime}
          </div>
          <div className="sign-box">
            <div style={{ height: 36 }} />
            <div className="sign-line">Authorized Signatory</div>
            <div className="sign-hint">Stores / Fleet Administration</div>
          </div>
        </div>

        <div className="rpt-footer">
          <span>
            INFRACONSOFT · ERP Software · www.infraconsoft.com · owned by
            www.nissiinfotech.com
          </span>
          <span>Confidential — Internal Use Only</span>
        </div>

        <p className="disclaimer">
          This is a computer-generated report. No physical signature is required
          for authenticity unless mandated by company policy.
        </p>
      </div>
    </div>
  );
};

export default BatteryCardMaintenance_AllPrint;